import { Box, FormControl, MenuItem, Select, TextField } from "@mui/material"
import { HelpOutlined, KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import type { ControllerRenderProps, FieldError } from "react-hook-form"

import { FormField } from "../Forms"

interface TextFieldWithSelectProps {
  name: string
  label?: string
  selectName: string
  options: Record<string, string>
  placeholder?: string
  required?: boolean
  className?: string
  slotProps?: {
    label?: {
      className?: string
    }
    error?: {
      className?: string
    }
  }
  shouldHideError?: boolean
}

interface TextFieldWithSelectContentProps {
  field: ControllerRenderProps
  fieldState: { error?: FieldError }
  selectName: string
  options: Record<string, string>
  placeholder?: string
  shouldHideError?: boolean
}

const TextFieldWithSelectContent = ({
  field,
  fieldState: { error },
  selectName,
  options,
  placeholder = "0",
  shouldHideError = false,
}: TextFieldWithSelectContentProps) => (
  <TextField
    {...field}
    type='number'
    value={field.value ?? ""}
    onChange={(e) => {
      const value = e.target.value
      // Only convert to number if we have a value
      field.onChange(value === "" ? null : Number(value))
    }}
    onFocus={(e) => {
      const value = e.target.value
      if (!value || value === "0") {
        field.onChange(null)
      }
    }}
    slotProps={{
      input: {
        endAdornment: (
          <Box className='flex items-center gap-4'>
            <HelpOutlined />
            <FormField
              name={selectName}
              shouldHideError={shouldHideError}
              render={({ field: selectField }) => (
                <EndAdornmentSelect selectField={selectField}>
                  {Object.entries(options).map(([key, value]) => (
                    <MenuItem key={key} value={key} onChange={(e) => selectField.onChange(e.target.value)}>
                      {value}
                    </MenuItem>
                  ))}
                </EndAdornmentSelect>
              )}
            />
          </Box>
        ),
      },
      htmlInput: {
        className: "hide-stepper",
      },
    }}
    placeholder={placeholder}
    fullWidth
    variant='outlined'
    error={!!error}
  />
)

export const TextFieldWithSelect = ({
  name,
  label,
  selectName,
  options,
  placeholder = "0",
  required = false,
  slotProps,
  shouldHideError = false,
}: TextFieldWithSelectProps): React.ReactElement => {
  return (
    <Box className='flex grow flex-col gap-1'>
      <FormField
        name={name}
        label={label}
        required={required}
        slotProps={slotProps}
        render={(props) => (
          <TextFieldWithSelectContent
            {...props}
            selectName={selectName}
            options={options}
            placeholder={placeholder}
            shouldHideError={shouldHideError}
          />
        )}
      />
    </Box>
  )
}
export const EndAdornmentSelect: FC<{ selectField: any; children: React.ReactNode }> = ({ selectField, children }) => {
  return (
    <FormControl variant='outlined' size='small'>
      <Select
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          minWidth: "60px",
          "& .MuiSelect-select": {
            padding: "2px 4px",
          },
        }}
        {...selectField}
        displayEmpty
        IconComponent={KeyboardArrowDownOutlined}
        MenuProps={{
          PaperProps: {
            style: { maxHeight: 48 * 4.5 + 8 },
          },
        }}
        inputProps={{ "aria-label": "Without label" }}
      >
        {children}
      </Select>
    </FormControl>
  )
}
